/**
 * Interaction Hook for WebGLChart
 * Handles zoom, pan, and touch interactions
 */

import { useEffect, useRef, useCallback } from 'react';
import { ZoomState, Bounds, BrushRange } from '../types';
import { ZOOM_FACTOR, MAX_ZOOM, MIN_ZOOM, DRAG_THRESHOLD } from '../constants';

export interface DragSelection {
  startX: number;
  endX: number;
  startY: number;
  endY: number;
  active: boolean;
}

export interface HoverPosition {
  x: number;  // Screen X relative to canvas
  y: number;  // Screen Y relative to canvas
  normX: number; // Normalized X in data space (-1 to 1 before zoom)
}

interface UseInteractionProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  zoomState: ZoomState;
  onZoomChange: (zoom: ZoomState) => void;
  initialBounds: React.RefObject<Bounds | null>;
  onSharedBrushChange?: (range: BrushRange | null) => void;
  lastSentRangeRef: React.MutableRefObject<{ startTime: number; endTime: number } | null>;
  onDragSelectionChange?: (selection: DragSelection | null) => void;
  onHoverChange?: (hover: HoverPosition | null) => void;
  /** Given the new x zoom, return scaleY/offsetY that fit y to the visible-x data (or null to keep y). */
  computeYFit?: (scaleX: number, offsetX: number) => { scaleY: number; offsetY: number } | null;
}

export function useInteraction({
  canvasRef,
  zoomState,
  onZoomChange,
  initialBounds,
  onSharedBrushChange,
  lastSentRangeRef,
  onDragSelectionChange,
  onHoverChange,
  computeYFit,
}: UseInteractionProps) {
  const isDraggingRef = useRef(false);
  const isPanningRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const zoomStateRef = useRef(zoomState);
  const computeYFitRef = useRef(computeYFit);
  useEffect(() => { computeYFitRef.current = computeYFit; }, [computeYFit]);
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const hoverThrottleRef = useRef<number | null>(null);
  const syncRafRef = useRef<number | null>(null);

  // Keep zoom state ref updated
  useEffect(() => {
    zoomStateRef.current = zoomState;
  }, [zoomState]);

  // Notify parent about zoom change for sync
  const notifySync = useCallback((zoom: ZoomState) => {
    if (!onSharedBrushChange || !initialBounds.current) {
      return;
    }

    const { xMin, xMax } = initialBounds.current;
    const xRange = xMax - xMin;

    // Convert zoom state back to time range
    const centerOffset = zoom.offsetX / 2;
    const visibleWidth = xRange / zoom.scaleX;
    const visibleCenter = xMin + xRange * 0.5 - centerOffset * xRange / zoom.scaleX;
    const visibleStart = visibleCenter - visibleWidth / 2;
    const visibleEnd = visibleCenter + visibleWidth / 2;

    const range = {
      startTime: visibleStart * 1000,
      endTime: visibleEnd * 1000,
    };

    // Store what we're sending so we can detect our own sync
    lastSentRangeRef.current = range;
    onSharedBrushChange(range);
  }, [onSharedBrushChange, initialBounds, lastSentRangeRef]);

  // Schedule sync via RAF for smooth rendering (max 60 syncs/sec)
  const scheduleSync = useCallback((zoom: ZoomState) => {
    if (syncRafRef.current) {
      cancelAnimationFrame(syncRafRef.current);
    }
    syncRafRef.current = requestAnimationFrame(() => {
      notifySync(zoom);
    });
  }, [notifySync]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (syncRafRef.current) {
        cancelAnimationFrame(syncRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Merge a fitted y (from visible-x data) into an x-changed zoom.
    const withYFit = (z: ZoomState): ZoomState => {
      const fit = computeYFitRef.current?.(z.scaleX, z.offsetX);
      return fit ? { ...z, scaleY: fit.scaleY, offsetY: fit.offsetY } : z;
    };

    // WHEEL ZOOM (X-axis only, centered on cursor)
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cursorX = (e.clientX - rect.left) / rect.width; // 0 to 1

      const currentZoom = zoomStateRef.current;
      const zoomChange = e.deltaY > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
      const newScaleX = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentZoom.scaleX * zoomChange));

      // Calculate offset to zoom toward cursor position
      // cursor position in normalized coords (-1 to 1)
      const cursorNorm = cursorX * 2 - 1;
      // Adjust offset so the point under cursor stays fixed
      const offsetChange = cursorNorm * (1 - zoomChange);
      const newOffsetX = currentZoom.offsetX * zoomChange + offsetChange * (currentZoom.scaleX / newScaleX);

      const newZoom: ZoomState = {
        ...currentZoom,
        scaleX: newScaleX,
        offsetX: newOffsetX,
      };

      const fitted = withYFit(newZoom);
      onZoomChange(fitted);
      scheduleSync(fitted);
    };

    // WINDOW-LEVEL DRAG / PAN - track past the canvas edge with clamping
    function clampToRect(clientX: number, clientY: number, rect: DOMRect) {
      const x = Math.max(rect.left, Math.min(rect.right, clientX));
      const y = Math.max(rect.top, Math.min(rect.bottom, clientY));
      return { x, y };
    }

    function handleWindowMove(e: MouseEvent) {
      const cv = canvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();

      if (isPanningRef.current) {
        const dx = (e.clientX - lastPosRef.current.x) / cv.width;
        lastPosRef.current = { x: e.clientX, y: e.clientY };
        const currentZoom = zoomStateRef.current;
        onZoomChange({ ...currentZoom, offsetX: currentZoom.offsetX + dx * 2 });
        return;
      }
      if (isDraggingRef.current && onDragSelectionChange) {
        const { x, y } = clampToRect(e.clientX, e.clientY, rect);
        onDragSelectionChange({
          startX: dragStartXRef.current,
          endX: x - rect.left,
          startY: dragStartYRef.current,
          endY: y - rect.top,
          active: true,
        });
      }
    }

    function handleWindowUp(e: MouseEvent) {
      window.removeEventListener('mousemove', handleWindowMove);
      window.removeEventListener('mouseup', handleWindowUp);

      const cv = canvasRef.current;
      if (onDragSelectionChange) onDragSelectionChange(null);

      if (isPanningRef.current) {
        isPanningRef.current = false;
        // Pan changed x only → refit y, then sync.
        const fitted = withYFit(zoomStateRef.current);
        onZoomChange(fitted);
        scheduleSync(fitted);
        return;
      }
      if (!isDraggingRef.current || !cv) return;
      isDraggingRef.current = false;

      const rect = cv.getBoundingClientRect();
      const start = clampToRect(dragStartRef.current.x, dragStartRef.current.y, rect);
      const end = clampToRect(e.clientX, e.clientY, rect);
      const dx = Math.abs(end.x - start.x);
      const dy = Math.abs(end.y - start.y);
      if (dx <= DRAG_THRESHOLD && dy <= DRAG_THRESHOLD) return;

      // Same select→zoom math as the old handleMouseUp, but on clamped coords.
      const startX = (start.x - rect.left) / rect.width;
      const endX = (end.x - rect.left) / rect.width;
      const startY = (start.y - rect.top) / rect.height;
      const endY = (end.y - rect.top) / rect.height;
      const minX = Math.min(startX, endX), maxX = Math.max(startX, endX);
      const minY = Math.min(startY, endY), maxY = Math.max(startY, endY);
      const selectionWidth = maxX - minX;
      const selectionHeight = maxY - minY;
      if (selectionWidth <= 0 && selectionHeight <= 0) return;

      const currentZoom = zoomStateRef.current;
      const screenToNormX = (sx: number) => (sx * 2 - 1) / currentZoom.scaleX - currentZoom.offsetX / currentZoom.scaleX;
      const screenToNormY = (sy: number) => (1 - sy * 2) / currentZoom.scaleY - currentZoom.offsetY / currentZoom.scaleY;
      const selMinNormX = screenToNormX(minX);
      const selMaxNormX = screenToNormX(maxX);
      const selCenterNormX = (selMinNormX + selMaxNormX) / 2;
      const selWidthNorm = selMaxNormX - selMinNormX;
      const selMinNormY = screenToNormY(maxY);
      const selMaxNormY = screenToNormY(minY);
      const selCenterNormY = (selMinNormY + selMaxNormY) / 2;
      const selHeightNorm = selMaxNormY - selMinNormY;
      const newScaleX = selWidthNorm > 0 ? Math.min(MAX_ZOOM, 2 / selWidthNorm) : currentZoom.scaleX;
      const newScaleY = selHeightNorm > 0 ? Math.min(MAX_ZOOM, 2 / selHeightNorm) : currentZoom.scaleY;
      const newOffsetX = selWidthNorm > 0 ? -selCenterNormX * newScaleX : currentZoom.offsetX;
      const newOffsetY = selHeightNorm > 0 ? -selCenterNormY * newScaleY : currentZoom.offsetY;
      const newZoom: ZoomState = { ...currentZoom, scaleX: newScaleX, scaleY: newScaleY, offsetX: newOffsetX, offsetY: newOffsetY };
      onZoomChange(newZoom);
      scheduleSync(newZoom);
    }

    // MOUSE DOWN - Start drag or pan
    const handleMouseDown = (e: MouseEvent) => {
      if (onHoverChange) onHoverChange(null);
      const rect = canvas.getBoundingClientRect();

      if (e.button === 0) {
        // Left click - drag select (rectangular)
        isDraggingRef.current = true;
        dragStartRef.current = { x: e.clientX, y: e.clientY };
        lastPosRef.current = { x: e.clientX, y: e.clientY };

        // Record starting position relative to canvas
        dragStartXRef.current = e.clientX - rect.left;
        dragStartYRef.current = e.clientY - rect.top;

        // Start selection overlay
        if (onDragSelectionChange) {
          onDragSelectionChange({
            startX: dragStartXRef.current,
            endX: dragStartXRef.current,
            startY: dragStartYRef.current,
            endY: dragStartYRef.current,
            active: true,
          });
        }

        // Track drag on window so releasing past the axis still commits (Bug #1)
        window.addEventListener('mousemove', handleWindowMove);
        window.addEventListener('mouseup', handleWindowUp);
      } else if (e.button === 1) {
        // Middle click - pan
        e.preventDefault();
        isPanningRef.current = true;
        lastPosRef.current = { x: e.clientX, y: e.clientY };

        // Track pan on window so releasing past the canvas still commits
        window.addEventListener('mousemove', handleWindowMove);
        window.addEventListener('mouseup', handleWindowUp);
      }
    };

    // MOUSE MOVE - Handle drag, pan, and hover
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current || isPanningRef.current) return; // window handler owns it
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (isPanningRef.current) {
        // Panning mode
        const dx = (e.clientX - lastPosRef.current.x) / canvas.width;
        lastPosRef.current = { x: e.clientX, y: e.clientY };

        const currentZoom = zoomStateRef.current;
        const newZoom: ZoomState = {
          ...currentZoom,
          offsetX: currentZoom.offsetX + dx * 2,
        };

        onZoomChange(newZoom);

        // Clear hover during pan
        if (onHoverChange) {
          onHoverChange(null);
        }
      } else if (isDraggingRef.current && onDragSelectionChange) {
        // Update selection overlay during drag (rectangular)
        onDragSelectionChange({
          startX: dragStartXRef.current,
          endX: mouseX,
          startY: dragStartYRef.current,
          endY: mouseY,
          active: true,
        });

        // Clear hover during drag
        if (onHoverChange) {
          onHoverChange(null);
        }
      } else if (onHoverChange) {
        // Hover mode - throttle updates for performance
        if (hoverThrottleRef.current) {
          cancelAnimationFrame(hoverThrottleRef.current);
        }

        hoverThrottleRef.current = requestAnimationFrame(() => {
          const currentZoom = zoomStateRef.current;

          // Convert screen position to normalized data coordinate
          const screenNormX = mouseX / rect.width; // 0 to 1
          // screenNormX maps to WebGL visible range
          // visible WebGL X = (screenX * 2 - 1) at scale 1
          // with zoom: normX = (screenNormX * 2 - 1) / scale - offset / scale
          const normX = (screenNormX * 2 - 1) / currentZoom.scaleX - currentZoom.offsetX / currentZoom.scaleX;

          onHoverChange({
            x: mouseX,
            y: mouseY,
            normX,
          });
        });
      }
    };

    // MOUSE LEAVE - Clear hover (drag/pan end is handled by the window mouseup)
    const handleMouseLeave = () => {
      if (onHoverChange) onHoverChange(null);
    };

    // DOUBLE-CLICK - Reset zoom
    const handleDblClick = () => {
      const resetZoom: ZoomState = {
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        xMin: initialBounds.current?.xMin ?? 0,
        xMax: initialBounds.current?.xMax ?? 1,
      };

      onZoomChange(resetZoom);

      if (onSharedBrushChange && initialBounds.current) {
        const range = {
          startTime: initialBounds.current.xMin * 1000,
          endTime: initialBounds.current.xMax * 1000,
        };
        // Store what we're sending so we can detect our own sync
        lastSentRangeRef.current = range;
        onSharedBrushChange(range);
      }
    };

    // TOUCH - Pinch to zoom
    let initialTouchDistance = 0;
    let initialScale = 1;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        initialTouchDistance = Math.sqrt(dx * dx + dy * dy);
        initialScale = zoomStateRef.current.scaleX;
      } else if (e.touches.length === 1) {
        isDraggingRef.current = true;
        lastPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        const scale = (distance / initialTouchDistance) * initialScale;
        const newScaleX = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));

        const currentZoom = zoomStateRef.current;
        onZoomChange({
          ...currentZoom,
          scaleX: newScaleX,
        });
      } else if (e.touches.length === 1 && isDraggingRef.current) {
        // Single finger pan
        const dx = (e.touches[0].clientX - lastPosRef.current.x) / canvas.width;
        lastPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

        const currentZoom = zoomStateRef.current;
        onZoomChange({
          ...currentZoom,
          offsetX: currentZoom.offsetX + dx * 2,
        });
      }
    };

    const handleTouchEnd = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        scheduleSync(zoomStateRef.current);
      }
    };

    // Prevent context menu on right click
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    // Add event listeners
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('dblclick', handleDblClick);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('contextmenu', handleContextMenu);

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('dblclick', handleDblClick);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('contextmenu', handleContextMenu);

      // Defensively remove any lingering window drag/pan listeners
      window.removeEventListener('mousemove', handleWindowMove);
      window.removeEventListener('mouseup', handleWindowUp);

      // Cleanup throttle
      if (hoverThrottleRef.current) {
        cancelAnimationFrame(hoverThrottleRef.current);
      }
    };
  }, [canvasRef, onZoomChange, initialBounds, scheduleSync, onSharedBrushChange, lastSentRangeRef, onDragSelectionChange, onHoverChange]);
}
